import getConfig from 'next/config';
import fetch from 'isomorphic-fetch';

import * as SERVER_SIDE_CAS_HELPERS from './logToCasServerSide';
import { getCasID } from './cookies';
import {
  getGenericStorageItem,
  removeGenericStorageItem,
  setGenericStorageItem,
  STORAGE_KEYS,
} from './storage';

const { CasLogLevel, safelyToString } = SERVER_SIDE_CAS_HELPERS;

const { publicRuntimeConfig } = getConfig();
const { ENVIRONMENT } = publicRuntimeConfig;

const NewRelicLogLevel = {
  Debug: 'debug',
  Info: 'info',
  Warn: 'warn',
  Error: 'error',
};

const LogLevel = {
  NotSet: 'notSet',
  Debug: 'debug',
  Info: 'info',
  Warn: 'warn',
  Error: 'error',
};

export {
  CasLogLevel,
  NewRelicLogLevel,
  LogLevel,
  safelyToString,
};

function mapLogLevelToCasLogLevel(level) {
  switch (level) {
    case LogLevel.Debug:
      return CasLogLevel.Debug;
    case LogLevel.Info:
      return CasLogLevel.Information;
    case LogLevel.Warn:
      return CasLogLevel.Information; // CAS doesn't have a warning level, so log as info
    case LogLevel.Error:
      return CasLogLevel.Error;
    default:
      return CasLogLevel.Information;
  }
}

function mapLogLevelToNewRelicLogLevel(level) {
  switch (level) {
    case LogLevel.Debug:
      return NewRelicLogLevel.Debug;
    case LogLevel.Info:
      return NewRelicLogLevel.Info;
    case LogLevel.Warn:
      return NewRelicLogLevel.Warn;
    case LogLevel.Error:
      return NewRelicLogLevel.Error;
    default:
      return NewRelicLogLevel.Info;
  }
}

function acceptLogLevelToCas(logLevel) {
  const clientCasLogLevel = parseInt(ENVIRONMENT?.ClientCasLogLevel ?? '4', 10);
  return mapLogLevelToCasLogLevel(logLevel) >= clientCasLogLevel;
}

function acceptLogLevelToNewRelic(logLevel) {
  const clientNewRelicLogLevel = ENVIRONMENT?.ClientNewRelicLogLevel ?? NewRelicLogLevel.Error;
  return Object.values(NewRelicLogLevel).indexOf(mapLogLevelToNewRelicLogLevel(logLevel))
    >= Object.values(NewRelicLogLevel).indexOf(clientNewRelicLogLevel);
}

function persistSessionIdForNewRelic() {
  const persistedSessionId = getGenericStorageItem(STORAGE_KEYS.NEW_RELIC_PERSISTED_SESSION_ID);
  const casId = getCasID();
  if (casId) {
    if (!persistedSessionId || persistedSessionId !== casId) {
      if (window.newrelic) {
        setGenericStorageItem(STORAGE_KEYS.NEW_RELIC_PERSISTED_SESSION_ID, casId);
        window.newrelic.setCustomAttribute('SessionId', casId);
      }
    }
  } else if (persistedSessionId) {
    removeGenericStorageItem(STORAGE_KEYS.NEW_RELIC_PERSISTED_SESSION_ID);
    if (window.newrelic) {
      window.newrelic.setCustomAttribute('SessionId', null);
    }
  }
}

export default function logToClientSide(logLevel, controller = '', action = '', error = null) {
  if (typeof window === 'undefined') return; // client side only

  // log to New Relic
  if (acceptLogLevelToNewRelic(logLevel) && window.newrelic?.log && typeof window.newrelic.log === 'function') {
    persistSessionIdForNewRelic();
    try {
      window.newrelic.log(`${controller}:${action}`, { level: mapLogLevelToNewRelicLogLevel(logLevel) });
    } catch (e) {
      console.error('Error logging to New Relic:', e);
    }
  }

  // log to CAS
  if (acceptLogLevelToCas(logLevel)) {
    let body = '';
    try {
      body = JSON.stringify(error, Object.getOwnPropertyNames(error));
    } catch (e) {
      body = 'Problem parsing error';
    }

    const url = `${ENVIRONMENT?.IBEUrl}/api/Logger/PostLogItem`;
    const data = JSON.stringify({
      auditType: mapLogLevelToCasLogLevel(logLevel),
      controller: `UI - ${controller}`,
      action,
      body,
    });

    fetch(url, {
      method: 'POST',
      headers: {
        'Cache-Control': 'no-store',
        'Content-Type': 'application/json',
      },
      body: data,
    }).catch((e) => {
      console.error(e.message);
    });
  }
}
